/**
 * SPEC-20260507-2200: wrappers REST pra saga_variables.
 * RBAC: backend exige `platform-master` ou `platform-admin`.
 */

import { apiFetch } from "./auth";

interface ApiSuccess<T> {
  success: true;
  data: T;
}

export type SagaVariableValueType =
  | "string"
  | "number"
  | "boolean"
  | "date"
  | "datetime"
  | "choice"
  | "url";

export interface SagaVariableView {
  id: string;
  /** SPEC-2300: 4 escopos. */
  scope: "global" | "tenant" | "tenant-saga" | "tenant-saga-step";
  tenantKey: string | null;
  sagaType: string | null;
  /** SPEC-2300: stepId (X, Y, Z) scope. */
  stepId: string | null;
  name: string;
  valueType: SagaVariableValueType;
  /** Quando isSensitive=true, retorna "•••••" (mascarado). UI revela via olhinho. */
  value: string;
  choices: string[] | null;
  isSensitive: boolean;
  description: string | null;
  setBy: string | null;
  setAt: string;
  createdAt: string;
  updatedAt: string;
}

export interface UpsertVariableInput {
  name: string;
  valueType: SagaVariableValueType;
  value: string;
  choices?: string[];
  isSensitive?: boolean;
  description?: string;
}

export interface PlaceholderRequirement {
  name: string;
  origin: "placeholder" | "secret";
  isSensitive: boolean;
  type: SagaVariableValueType;
  hasValue: boolean;
  source: "tenant" | "global" | "default" | null;
  default?: string;
  description?: string;
  choices?: string[];
}

export interface PlaceholderRequirementsResponse {
  saga: string;
  tenantKey: string | null;
  required: PlaceholderRequirement[];
  builtins: Array<{ name: string; description: string }>;
}

export interface AutoFillSuggestion {
  name: string;
  isSensitive: boolean;
  suggestedValue: string | null;
  type: string;
  rationale?: string;
}

// ─── Globals ─────────────────────────────────────────────────────────────────

export async function listGlobalVariables(): Promise<SagaVariableView[]> {
  const r = await apiFetch<ApiSuccess<{ variables: SagaVariableView[] }>>(
    "/api/saga-variables/global",
  );
  return r.data.variables;
}

export async function upsertGlobalVariable(input: UpsertVariableInput): Promise<SagaVariableView> {
  const r = await apiFetch<ApiSuccess<SagaVariableView>>(
    `/api/saga-variables/global/${encodeURIComponent(input.name)}`,
    { method: "PUT", body: JSON.stringify(input) },
  );
  return r.data;
}

export async function deleteGlobalVariable(name: string): Promise<void> {
  await apiFetch<ApiSuccess<{ ok: boolean }>>(
    `/api/saga-variables/global/${encodeURIComponent(name)}`,
    { method: "DELETE" },
  );
}

export async function revealGlobalVariable(name: string): Promise<string> {
  const r = await apiFetch<ApiSuccess<{ name: string; value: string }>>(
    `/api/saga-variables/global/${encodeURIComponent(name)}/reveal`,
    { method: "POST", body: "{}" },
  );
  return r.data.value;
}

// ─── Tenant only (X, null, null) — SPEC-2300 ────────────────────────────────

export async function listTenantOnlyVariables(tenantKey: string): Promise<SagaVariableView[]> {
  const r = await apiFetch<
    ApiSuccess<{ tenantKey: string; scope: "tenant"; variables: SagaVariableView[] }>
  >(`/api/tenants/${encodeURIComponent(tenantKey)}/saga-variables/_tenant`);
  return r.data.variables;
}

export async function upsertTenantOnlyVariable(
  tenantKey: string,
  input: UpsertVariableInput,
): Promise<SagaVariableView> {
  const r = await apiFetch<ApiSuccess<SagaVariableView>>(
    `/api/tenants/${encodeURIComponent(tenantKey)}/saga-variables/_tenant/${encodeURIComponent(input.name)}`,
    { method: "PUT", body: JSON.stringify(input) },
  );
  return r.data;
}

export async function deleteTenantOnlyVariable(tenantKey: string, name: string): Promise<void> {
  await apiFetch<ApiSuccess<{ ok: boolean }>>(
    `/api/tenants/${encodeURIComponent(tenantKey)}/saga-variables/_tenant/${encodeURIComponent(name)}`,
    { method: "DELETE" },
  );
}

export async function revealTenantOnlyVariable(tenantKey: string, name: string): Promise<string> {
  const r = await apiFetch<ApiSuccess<{ name: string; value: string }>>(
    `/api/tenants/${encodeURIComponent(tenantKey)}/saga-variables/_tenant/${encodeURIComponent(name)}/reveal`,
    { method: "POST", body: "{}" },
  );
  return r.data.value;
}

// ─── Tenant + sagaType ───────────────────────────────────────────────────────

export interface TenantSagaVariableGroup {
  sagaType: string | null;
  stepId: string | null;
  variables: SagaVariableView[];
}

export async function listTenantVariables(tenantKey: string): Promise<{
  tenantKey: string;
  groups: TenantSagaVariableGroup[];
}> {
  const r = await apiFetch<
    ApiSuccess<{ tenantKey: string; groups: TenantSagaVariableGroup[] }>
  >(`/api/tenants/${encodeURIComponent(tenantKey)}/saga-variables`);
  return r.data;
}

export async function listTenantSagaVariables(
  tenantKey: string,
  sagaType: string,
): Promise<SagaVariableView[]> {
  const r = await apiFetch<
    ApiSuccess<{ tenantKey: string; sagaType: string; variables: SagaVariableView[] }>
  >(
    `/api/tenants/${encodeURIComponent(tenantKey)}/saga-variables/${encodeURIComponent(sagaType)}`,
  );
  return r.data.variables;
}

export async function upsertTenantSagaVariables(
  tenantKey: string,
  sagaType: string,
  variables: Record<string, Omit<UpsertVariableInput, "name">>,
): Promise<SagaVariableView[]> {
  const r = await apiFetch<ApiSuccess<{ variables: SagaVariableView[] }>>(
    `/api/tenants/${encodeURIComponent(tenantKey)}/saga-variables/${encodeURIComponent(sagaType)}`,
    { method: "PUT", body: JSON.stringify({ variables }) },
  );
  return r.data.variables;
}

export async function deleteTenantSagaVariable(
  tenantKey: string,
  sagaType: string,
  name: string,
): Promise<void> {
  await apiFetch<ApiSuccess<{ ok: boolean }>>(
    `/api/tenants/${encodeURIComponent(tenantKey)}/saga-variables/${encodeURIComponent(sagaType)}/${encodeURIComponent(name)}`,
    { method: "DELETE" },
  );
}

export async function setTenantSagaVariableSensitivity(
  tenantKey: string,
  sagaType: string,
  name: string,
  isSensitive: boolean,
): Promise<SagaVariableView> {
  const r = await apiFetch<ApiSuccess<SagaVariableView>>(
    `/api/tenants/${encodeURIComponent(tenantKey)}/saga-variables/${encodeURIComponent(sagaType)}/${encodeURIComponent(name)}/sensitivity`,
    { method: "PATCH", body: JSON.stringify({ isSensitive }) },
  );
  return r.data;
}

export async function revealTenantSagaVariable(
  tenantKey: string,
  sagaType: string,
  name: string,
): Promise<string> {
  const r = await apiFetch<ApiSuccess<{ name: string; value: string }>>(
    `/api/tenants/${encodeURIComponent(tenantKey)}/saga-variables/${encodeURIComponent(sagaType)}/${encodeURIComponent(name)}/reveal`,
    { method: "POST", body: "{}" },
  );
  return r.data.value;
}

export async function autoFillTenantSagaVariables(
  tenantKey: string,
  sagaType: string,
  onlyMissing = true,
): Promise<AutoFillSuggestion[]> {
  const r = await apiFetch<ApiSuccess<{ suggestions: AutoFillSuggestion[] }>>(
    `/api/tenants/${encodeURIComponent(tenantKey)}/saga-variables/${encodeURIComponent(sagaType)}/auto-fill`,
    { method: "POST", body: JSON.stringify({ onlyMissing }) },
  );
  return r.data.suggestions;
}

// ─── Tenant + saga + step (X, Y, Z) — SPEC-2300 ─────────────────────────────

export async function listStepVariables(
  tenantKey: string,
  sagaType: string,
  stepId: string,
): Promise<SagaVariableView[]> {
  const r = await apiFetch<
    ApiSuccess<{
      tenantKey: string;
      sagaType: string;
      stepId: string;
      variables: SagaVariableView[];
    }>
  >(
    `/api/tenants/${encodeURIComponent(tenantKey)}/saga-variables/${encodeURIComponent(sagaType)}/steps/${encodeURIComponent(stepId)}`,
  );
  return r.data.variables;
}

export async function upsertStepVariables(
  tenantKey: string,
  sagaType: string,
  stepId: string,
  variables: Record<string, Omit<UpsertVariableInput, "name">>,
): Promise<SagaVariableView[]> {
  const r = await apiFetch<ApiSuccess<{ variables: SagaVariableView[] }>>(
    `/api/tenants/${encodeURIComponent(tenantKey)}/saga-variables/${encodeURIComponent(sagaType)}/steps/${encodeURIComponent(stepId)}`,
    { method: "PUT", body: JSON.stringify({ variables }) },
  );
  return r.data.variables;
}

export async function deleteStepVariable(
  tenantKey: string,
  sagaType: string,
  stepId: string,
  name: string,
): Promise<void> {
  await apiFetch<ApiSuccess<{ ok: boolean }>>(
    `/api/tenants/${encodeURIComponent(tenantKey)}/saga-variables/${encodeURIComponent(sagaType)}/steps/${encodeURIComponent(stepId)}/${encodeURIComponent(name)}`,
    { method: "DELETE" },
  );
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

export async function getPlaceholderRequirements(
  sagaName: string,
  tenantKey?: string | null,
): Promise<PlaceholderRequirementsResponse> {
  const qs = tenantKey ? `?tenantKey=${encodeURIComponent(tenantKey)}` : "";
  const r = await apiFetch<ApiSuccess<PlaceholderRequirementsResponse>>(
    `/api/saga-variables/saga-definitions/${encodeURIComponent(sagaName)}/placeholder-requirements${qs}`,
  );
  return r.data;
}

export async function listBuiltins(): Promise<Array<{ name: string; description: string }>> {
  const r = await apiFetch<ApiSuccess<{ builtins: Array<{ name: string; description: string }> }>>(
    "/api/saga-variables/builtins",
  );
  return r.data.builtins;
}
